import {createSelector} from 'reselect';
import {
    getTranslations,
    getTripData,
    getDatePicker
} from './store';
import {
    getDeviceSize,
    getIsMobile,
    getLocale
} from './system';
import {getAvailabilityData, getShouldShowRates} from './availability';

const getCalendarLabels = createSelector(
    [getTranslations],
    (translations) => {
        const monthNames = [translations.monthJanuary, translations.monthFebruary, translations.monthMarch, translations.monthApril, translations.monthMay, translations.monthJune, translations.monthJuly, translations.monthAugust, translations.monthSeptember, translations.monthOctober, translations.monthNovember, translations.monthDecember];
        const shortMonthNames = [translations.monthJanuaryShort, translations.monthFebruaryShort, translations.monthMarchShort, translations.monthAprilShort, translations.monthMayShort, translations.monthJuneShort, translations.monthJulyShort, translations.monthAugustShort, translations.monthSeptemberShort, translations.monthOctoberShort, translations.monthNovemberShort, translations.monthDecemberShort];
        const dayNames = [translations.daySunday, translations.dayMonday, translations.dayTuesday, translations.dayWednesday, translations.dayThursday, translations.dayFriday, translations.daySaturday];
        const shortDayNames = [translations.daySundayLong, translations.dayMondayLong, translations.dayTuesdayLong, translations.dayWednesdayLong, translations.dayThursdayLong, translations.dayFridayLong, translations.daySaturdayLong].map((day) => day && day.slice(0, 2));
        const yearLabel = translations.yearLabel === '??year.label??' ? '' : translations.yearLabel;

        const calendarLabels = {
            monthNames,
            shortMonthNames,
            dayNames,
            shortDayNames,
            yearLabel
        };

        return calendarLabels;
    }
);

const getLabels = createSelector(
    [getTranslations, getShouldShowRates, getCalendarLabels],
    (translations, shouldShowRates, calendarLabels) => {
        const mainHeading = shouldShowRates ? translations.ratesAvailability : translations.availability;

        const labels = {
            ratesAvailability: translations.ratesAvailability,
            availability: translations.availability,
            mainHeading,
            taxesAndFeesAdditional: translations.taxesAndFeesAdditional,
            ...calendarLabels
        };

        return labels;
    }
);

const getCalendarStartMonth = createSelector(
    [getTripData],
    (tripData) => {
        const {arriveDateText} = tripData;
        const dateParts = arriveDateText ? arriveDateText.replace('-0', '-').split('-') : null;

        return arriveDateText ? parseInt(dateParts[1], 10) - 1 : new Date().getMonth();
    }
);

const getCalendarStartYear = createSelector(
    [getTripData],
    (tripData) => {
        const {arriveDateText} = tripData;
        const dateParts = arriveDateText ? arriveDateText.replace('-0', '-').split('-') : null;

        return arriveDateText ? parseInt(dateParts[0], 10) : new Date().getUTCFullYear();
    }
);

const getDatePickerAvailability = createSelector(
    [getAvailabilityData, getDatePicker],
    (availability, datePicker) => {
        const datePickerAvailability = datePicker.selectingCheckout ? availability.datePickerAvailabilityData.checkout : availability.datePickerAvailabilityData.checkin;

        return datePickerAvailability;
    }
);

const getNumMonths = createSelector(
    [getDeviceSize],
    (deviceSize) => {
        return deviceSize === 'lg' || deviceSize === 'xl' ? 4 : 1;
    }
);

const getNumColumns = createSelector(
    [getDeviceSize],
    (deviceSize) => {
        return deviceSize === 'lg' || deviceSize === 'xl' ? 2 : 1;
    }
);

const getAvailabilityCalendar = createSelector(
    [getIsMobile, getLocale, getAvailabilityData, getLabels, getTripData, getCalendarStartMonth, getCalendarStartYear, getDatePickerAvailability, getNumMonths, getNumColumns, getShouldShowRates],
    (isMobile, locale, availability, labels, tripData, calendarStartMonth, calendarStartYear, datePickerAvailability, numMonths, numColumns, shouldShowRates) => {
        return {
            availability: availability.calendarAvailabilityData.dateless,
            calendarStartMonth,
            calendarStartYear,
            datePickerAvailability,
            isMobile,
            locale,
            labels,
            numMonths,
            numColumns,
            selectable: false,
            selectionEnd: tripData.departDateText,
            selectionStart: tripData.arriveDateText,
            shouldShowRates
        };
    }
);

export default getAvailabilityCalendar;
export {
    getLabels,
    getCalendarLabels,
    getCalendarStartMonth,
    getCalendarStartYear,
    getDatePickerAvailability,
    getNumMonths,
    getNumColumns,
    getAvailabilityCalendar
};
